import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges
} from '@angular/core';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { rxResourceFixed, BtnLoaderDirective } from '@zayan-erp/shared';
import { DealOptionsService, DepositService, SalesApsService } from '@zayan-erp/sales';
import {
  AbilityService,
  DropdownData,
  FileUploadService,
  GenericDropdownComponent,
  InputFieldComponent,
  LookupsService,
  CurrencyFormatPipe,
  CurrencyInputFieldComponent,
  ConfirmHelperService,
  LinkListDialog,
  DsmPaymentImage
} from '@zayan-erp/shared';
import { Deposit, DepositPayment } from '@zayan-erp/sales';
import { HttpEventType } from '@angular/common/http';
import { map, of, startWith, switchMap, tap } from 'rxjs';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePipe, NgTemplateOutlet } from '@angular/common';

type ChequeAttachment = {
  file: File | null;
  name: string;
  s3Key: string;
  documentId: string;
  s3Url: string;
  progress: number | null;
};

@Component({
  selector: 'deal-deposits-table',
  imports: [
    DatePickerModule,
    ToggleSwitchModule,
    NgbDropdownModule,
    GenericDropdownComponent,
    InputFieldComponent,
    CurrencyFormatPipe,
    FormsModule,
    ReactiveFormsModule,
    DatePipe,
    NgTemplateOutlet,
    CurrencyInputFieldComponent,
    LinkListDialog,
    DialogModule,
    BtnLoaderDirective
  ],
  host: { class: 'd-flex flex-column flex-fill overflow-hidden' },
  templateUrl: './deal-deposits-table.component.html',
  standalone: true
})
export class DealDepositsTableComponent implements OnChanges {
  deposits = input<Deposit[] | undefined>(undefined);
  sortedDeposits = computed(() => {
    const deps = this.deposits();
    if (!deps) return [];
    return [...deps].sort((a, b) => new Date(a.depositDueDate).getTime() - new Date(b.depositDueDate).getTime());
  });

  depositsSaved = output<void>();
  depositPayment = output<{ depositId: string; paymentId?: string }>();
  deleteDeposit = output<void>();
  deleteDepositPayment = output<void>();
  bulkDeleteDeposits = output<string[]>();
  imports = output<string | null>();

  _depositService = inject(DepositService);
  #dealOptionsService = inject(DealOptionsService);
  #lookupsService = inject(LookupsService);
  #salesApsService = inject(SalesApsService);
  confirmHelperService = inject(ConfirmHelperService);
  readonly #fileUploadService = inject(FileUploadService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #abilityService = inject(AbilityService);
  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));
  form = this._depositService.form;
  depositPaymentForm = this._depositService.depositPaymentForm;
  activeIndex: number | null = null;
  addendumRequiredValidators: boolean = false;
  selectedDeposits: Deposit[] = [];
  attachments = signal<ChequeAttachment[]>([]);

  viewImagesDialogVisible = signal(false);
  selectedPaymentImageLinks = signal<DsmPaymentImage[]>([]);

  isDepositSelected(deposit: Deposit): boolean {
    return this.selectedDeposits.some(d => d.id === deposit.id);
  }

  toggleDepositSelection(deposit: Deposit, checked: boolean): void {
    this.selectedDeposits = checked
      ? [...this.selectedDeposits.filter(d => d.id !== deposit.id), deposit]
      : this.selectedDeposits.filter(d => d.id !== deposit.id);
  }

  activePaymentDeposit(): Deposit | null {
    return this.sortedDeposits().find(deposit => deposit.id === this._depositService.depositId) ?? null;
  }

  depositTypes = toSignal(this._depositService.getDepositTypes(), { initialValue: [] as DropdownData[] });
  getAddendums = rxResourceFixed({
    params: () => ({ dealId: this.#salesApsService.currentDeal()?.id ?? '' }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.dealId) {
        return of([]);
      }
      return this.#dealOptionsService.getDealOptions(params.dealId).pipe(
        map(res => {
          return res.map<DropdownData>(item => ({
            id: item.id,
            firstLabel: item.builderReference.reference,
            secondLabel: `${item.addendumType.name} - ${item.seq}`,

            data: { hasValidOptions: (item.products ?? []).some(product => !product.isCancelled) }
          }));
        })
      );
    }
  });

  // Full list: used for labels, so existing deposits keep their addendum name.
  addendums = computed(() => {
    return this.getAddendums.value() ?? [];
  });

  readonly #selectedAddendumId = toSignal(
    this.form.controls.addendumId.valueChanges.pipe(startWith(this.form.controls.addendumId.value)),
    { initialValue: null }
  );

  addendumChoices = computed(() =>
    this.addendums().filter(addendum => addendum.data?.hasValidOptions || addendum.id === this.#selectedAddendumId())
  );
  tasks = toSignal(this.#lookupsService.readTasks(), { initialValue: [] as DropdownData[] });

  products = [{}];

  ngOnChanges(changes: SimpleChanges): void {
    this.getAddendums.reload();
  }

  resetForm(): void {
    this.form.reset();
    this._depositService.addNewDeposits = false;
  }

  onDepositPaymentFormSubmitted(deposit: Deposit): void {
    if (!this.canWriteDeals()) return;
    const documentIds = this.attachments()
      .filter(a => a.documentId)
      .map(a => a.documentId);
    this._depositService.depositPaymentForm.patchValue({ paymentImageIds: documentIds });

    if (this._depositService.paymentId) {
      this.depositPayment.emit({ depositId: deposit.id, paymentId: this._depositService.paymentId });
    } else {
      this.depositPayment.emit({ depositId: deposit.id });
    }
  }

  onAddPayment(deposit: Deposit): void {
    if (!this.canWriteDeals()) return;
    this._depositService.addPayment = true;
    this._depositService.depositId = deposit.id;
    this._depositService.paymentId = null;
    this.depositPaymentForm.reset();
    this.depositPaymentForm.patchValue({
      depositId: deposit.id
    });
    this.attachments.set([]);
    this.#salesApsService.realoadDeal.next(null);
  }

  onCancelPayment(): void {
    this._depositService.addPayment = false;
    this._depositService.depositId = null;
    this._depositService.paymentId = null;
    this.depositPaymentForm.reset();
    this.attachments.set([]);
  }

  onFileSelected(event: Event): void {
    if (!this.canWriteDeals()) return;
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      Array.from(input.files).forEach(file => this.#addAttachment(file));
    }
    input.value = '';
  }

  clearSelectedFiles(): void {
    this.attachments.set([]);
  }

  removeAttachment(attachment: ChequeAttachment): void {
    this.attachments.update(list => list.filter(a => a !== attachment));
  }

  #addAttachment(file: File): void {
    this.attachments.update(list => [
      ...list,
      { file, name: file.name, s3Key: '', documentId: '', s3Url: '', progress: 0 }
    ]);
    this.#trackUpload(file);
  }

  #trackUpload(file: File): void {
    this.#fileUploadService
      .getPrivateAWSSignedUrl(file)
      .pipe(
        switchMap(awsData =>
          this.#fileUploadService.uploadFileToS3(awsData).pipe(
            tap((uploadEvent: any) => {
              if (uploadEvent?.type === HttpEventType.UploadProgress && uploadEvent.total) {
                const progress = Math.round((100 * uploadEvent.loaded) / uploadEvent.total);
                this.attachments.update(list =>
                  list.map(a =>
                    a.file === file ? { ...a, progress, s3Key: awsData.s3Key, documentId: awsData.documentId } : a
                  )
                );
              }
            })
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.attachments.update(list => list.map(a => (a.file === file ? { ...a, progress: null } : a)));
      });
  }

  getAddendumLabel(addendumId: string | null | undefined): string {
    if (!addendumId) return '';
    const addendum = this.addendums()?.find(a => a?.id === addendumId);
    return addendum?.firstLabel ?? '';
  }

  onEditPayment(payment: DepositPayment): void {
    if (!this.canWriteDeals()) return;
    this.attachments.set(
      (payment.paymentImages ?? []).map(img => ({
        file: null,
        name: 'Saved cheque image',
        s3Key: img.s3Key,
        documentId: img.id,
        s3Url: img.directUrl,
        progress: null
      }))
    );
    this._depositService.addPayment = true;
    this._depositService.depositId = payment.depositId;
    this._depositService.paymentId = payment?.id;
    this.depositPaymentForm.patchValue({
      depositId: payment.depositId,
      paymentDate: new Date(payment.paymentDate),
      paymentAmount: payment.paymentAmount,
      referenceNo: payment.referenceNo,
      returned: payment.returned
    });
  }

  onEditDeposit(deposit: Deposit): void {
    if (!this.canWriteDeals()) return;
    this._depositService.depositId = deposit?.id;
    this._depositService.addNewDeposits = true;
    this.form.patchValue({
      depositTypeId: deposit.depositType.id,
      addendumId: deposit.addendumId,
      taskId: deposit.taskId,
      depositDueDate: new Date(deposit.depositDueDate),
      depositAmount: deposit.depositAmount
    });
  }

  getMilestoneLabel(taskId: string | null | undefined): string {
    if (!taskId) return '';
    const task = this.tasks()?.find(t => t?.id === taskId);
    return task?.secondLabel ?? '';
  }

  onDeleteDeposit(depositId: string): void {
    if (!this.canWriteDeals()) return;
    this._depositService.depositId = depositId;
    this.deleteDeposit.emit();
  }

  onDeleteDepositPayment(paymentId: string): void {
    if (!this.canWriteDeals()) return;
    this._depositService.paymentId = paymentId;
    this.deleteDepositPayment.emit();
  }

  onToggleReturned(payment: DepositPayment): void {
    if (!this.canWriteDeals()) return;
    this._depositService.paymentId = payment.id;
    this._depositService.depositPaymentForm.patchValue({
      depositId: payment.depositId,
      paymentDate: new Date(payment.paymentDate),
      paymentAmount: payment.paymentAmount,
      referenceNo: payment.referenceNo,
      returned: !payment.returned,
      paymentImageIds: (payment.paymentImages ?? []).map(img => img.id)
    });
    this.depositPayment.emit({ depositId: payment.depositId, paymentId: payment.id });
  }

  confirmBulkDelete() {
    if (!this.canWriteDeals()) return;
    let count = this.selectedDeposits.length;
    let depositAmount = 0;

    this.selectedDeposits.forEach(deposit => {
      depositAmount = depositAmount + deposit.depositAmount;
    });

    if (count === 0) return;

    this.confirmHelperService
      .showModal({
        icon: 'confirm-icon',
        header: 'Delete Selected Deposits?',
        message: `Are you sure you want to delete ${count} selected deposit(s) of total amount $${depositAmount}? This action cannot be undone.`,
        acceptLabel: 'Delete',
        acceptSeverity: 'danger',
        rejectLabel: 'Cancel'
      })
      .subscribe(confirmed => {
        if (confirmed) {
          const ids = this.selectedDeposits.map(d => d.id);
          this.bulkDeleteDeposits.emit(ids);
          this.selectedDeposits = [];
        }
      });
  }

  validatePaymentAmountForUpdate(deposit: Deposit): boolean {
    if (Number(this._depositService.depositPaymentForm?.controls?.paymentAmount?.value) > deposit?.depositAmount) {
      return false;
    }
    return true;
  }

  onDepositTypeChange(event: any): void {
    this.form.controls.addendumId.clearValidators();
    this.addendumRequiredValidators = false;
    if (this.depositTypes().find(x => x.id === event)?.firstLabel == 'Options') {
      this.form.controls.addendumId.setValidators([Validators.required]);
      this.addendumRequiredValidators = true;
    }
  }

  isSelectedDepositTypeOptions(): boolean {
    const typeId = this.form.controls.depositTypeId.value;
    const selected = this.depositTypes().find(x => x.id === typeId);
    return selected?.firstLabel === 'Options';
  }

  openPaymentImagesDialog(images: DsmPaymentImage[]): void {
    this.selectedPaymentImageLinks.set(images ?? []);
    this.viewImagesDialogVisible.set(true);
  }

  onDownloadReceipt(payment: DepositPayment): void {
    const lotNo = this.#salesApsService.currentDeal()?.lot?.lotNo;
    this._depositService.downloadReceipt(payment.id, `${lotNo}-Deposit-Slip-${+new Date()}.pdf`).subscribe({
      error: () => {}
    });
  }
}
